/**
 * Team and access — the Owner UI over the existing `/admin` backend.
 *
 * Every operation here is one the server already implements and tests:
 * pending admissions, invitations, role changes, suspension and restoration.
 * The page adds no rule of its own. In particular, the last-active-Owner
 * protection is a **database constraint**; when the server refuses with a
 * conflict, this page explains the refusal rather than pre-checking it —
 * a pre-check would be a second, weaker copy of the rule, wrong exactly when
 * it matters (two owners demoting each other at once).
 *
 * A non-Owner reaching /team gets the shared "not available" page, because
 * the listings answer 403 — the navigation never offered the link, but the
 * URL is typeable and the answer must still be uninformative.
 */

import { useCallback, useState, type ReactNode } from 'react';

import {
  ApiFailure,
  changeAccountRole,
  createInvitation,
  deauthorizeAccount,
  decideAdmission,
  fetchAccessHistory,
  fetchAccounts,
  fetchAdmissions,
  fetchInvitations,
  restoreAccount,
  suspendAccount,
  withdrawInvitation,
  type AdminAccount,
} from '../api.js';
import { EventTime, useWorkspaceCopy } from '../components.js';
import { useSession } from '../session.js';
import {
  Button,
  ChoiceGroup,
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  Tag,
  TextField,
  useToasts,
} from '../ui.js';
import {
  FailurePanel,
  LoadingPanel,
  PageTitle,
  SectionHeading,
  useAsync,
} from './common.js';
import { NotAvailablePage } from './NotAvailablePage.js';

const ROLES = ['OWNER', 'DEVELOPER', 'ANNOTATOR'] as const;

export function TeamPage(): ReactNode {
  const copy = useWorkspaceCopy();
  const { session } = useSession();
  const { announce } = useToasts();

  const load = useCallback(async () => {
    const [accounts, invitations, admissions, history] = await Promise.all([
      fetchAccounts(),
      fetchInvitations(),
      fetchAdmissions(),
      fetchAccessHistory(),
    ]);
    return {
      accounts: accounts.accounts,
      invitations: invitations.invitations,
      admissions: admissions.admissions,
      history: history.entries,
    };
  }, []);

  const { state, reload } = useAsync(load);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string | null>('ANNOTATOR');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [lastOwnerRefused, setLastOwnerRefused] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const [roleDialog, setRoleDialog] = useState<AdminAccount | null>(null);
  const [pendingRole, setPendingRole] = useState<string | null>(null);
  const [suspendDialog, setSuspendDialog] = useState<AdminAccount | null>(null);
  const [deauthorizeDialog, setDeauthorizeDialog] = useState<AdminAccount | null>(null);

  const runAction = useCallback(
    (key: string, action: () => Promise<unknown>, after?: () => void) => {
      setBusyAction(key);
      setFailure(null);
      setLastOwnerRefused(false);
      action()
        .then(() => {
          setBusyAction(null);
          after?.();
          reload();
        })
        .catch((thrown: unknown) => {
          setBusyAction(null);
          const apiFailure =
            thrown instanceof ApiFailure ? thrown : new ApiFailure('failed');
          // The census refusal arrives as a conflict; that is the last-Owner
          // rule speaking, and the UI names it instead of a generic sentence.
          if (apiFailure.kind === 'conflict') setLastOwnerRefused(true);
          setFailure(apiFailure);
        });
    },
    [reload],
  );

  if (session.status === 'authenticated' && !session.profile.mayAdministerTasks) {
    return <NotAvailablePage />;
  }

  if (state.status === 'loading') return <LoadingPanel />;
  if (state.status === 'failed') {
    if (state.failure.kind === 'forbidden' || state.failure.kind === 'missing') {
      return <NotAvailablePage />;
    }
    return <FailurePanel failure={state.failure} onRetry={reload} />;
  }

  const { accounts, invitations, admissions, history } = state.value;
  const openInvitations = invitations.filter(
    (invitation) => invitation.status === 'open',
  );
  const pendingAdmissions = admissions.filter(
    (admission) => admission.status === 'pending',
  );

  return (
    <>
      <PageTitle>{copy.team.title}</PageTitle>

      {lastOwnerRefused ? (
        <InlineAlert tone="warning" title={copy.team.lastOwnerTitle} role="alert">
          {copy.team.lastOwnerBody}
        </InlineAlert>
      ) : failure !== null ? (
        <InlineAlert
          tone={failure.kind === 'offline' ? 'warning' : 'error'}
          title={
            failure.kind === 'offline'
              ? copy.states.offlineTitle
              : copy.states.failedTitle
          }
          role="alert"
        >
          {failure.detail ?? copy.states.failedBody}
        </InlineAlert>
      ) : null}

      {/* ------------------------------------------------ pending admissions */}
      <section
        aria-label={copy.team.pendingAdmissions}
        style={{ marginBottom: 'var(--space-xl)' }}
      >
        <SectionHeading>{copy.team.pendingAdmissions}</SectionHeading>
        {pendingAdmissions.length === 0 ? (
          <EmptyState title={copy.team.admissionsEmpty} compact />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="mj-table">
              <thead>
                <tr>
                  <th scope="col">{copy.team.provider}</th>
                  <th scope="col">{copy.team.providerEmail}</th>
                  <th scope="col">{copy.team.invitedAs}</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {pendingAdmissions.map((admission) => (
                  <tr key={admission.id}>
                    <td>{admission.provider}</td>
                    <td>
                      <span
                        className="mj-ltr"
                        dir="ltr"
                        style={{ font: 'var(--type-body-tabular)' }}
                      >
                        {admission.providerEmail ?? '—'}
                      </span>
                      {admission.providerEmail !== null &&
                      !admission.providerEmailVerified ? (
                        <>
                          {' '}
                          <Tag tone="warning">{copy.team.emailUnverified}</Tag>
                        </>
                      ) : null}
                    </td>
                    <td>
                      {admission.invitedEmail !== null ? (
                        <span
                          className="mj-ltr"
                          dir="ltr"
                          style={{ font: 'var(--type-body-tabular)' }}
                        >
                          {admission.invitedEmail}
                        </span>
                      ) : (
                        '—'
                      )}{' '}
                      {admission.invitedRole !== null ? (
                        <Tag tone="neutral">
                          {copy.roles[admission.invitedRole] ?? admission.invitedRole}
                        </Tag>
                      ) : null}
                    </td>
                    <td>
                      <div
                        style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}
                      >
                        <Button
                          size="sm"
                          variant="primary"
                          busy={busyAction === `admit-${admission.id}`}
                          onClick={() => {
                            runAction(`admit-${admission.id}`, () =>
                              decideAdmission(admission.id, 'approve'),
                            );
                          }}
                        >
                          {copy.team.approve}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          busy={busyAction === `reject-${admission.id}`}
                          onClick={() => {
                            runAction(`reject-${admission.id}`, () =>
                              decideAdmission(admission.id, 'reject'),
                            );
                          }}
                        >
                          {copy.team.reject}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ----------------------------------------------------- contributors */}
      <section
        aria-label={copy.team.contributors}
        style={{ marginBottom: 'var(--space-xl)' }}
      >
        <SectionHeading>{copy.team.contributors}</SectionHeading>
        <div style={{ overflowX: 'auto' }}>
          <table className="mj-table">
            <thead>
              <tr>
                <th scope="col">{copy.nav.account}</th>
                <th scope="col">{copy.team.inviteRole}</th>
                <th scope="col">{copy.contributions.columns.event}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td>{account.displayName}</td>
                  <td>
                    <Tag tone="neutral">{copy.roles[account.role] ?? account.role}</Tag>
                  </td>
                  <td>
                    <Tag tone={account.status === 'active' ? 'success' : 'warning'}>
                      {copy.team.statuses[account.status] ?? account.status}
                    </Tag>
                  </td>
                  <td>
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                      }}
                    >
                      {account.status !== 'deauthorized' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setRoleDialog(account);
                            setPendingRole(account.role);
                            setFailure(null);
                            setLastOwnerRefused(false);
                          }}
                        >
                          {copy.team.changeRole}
                        </Button>
                      ) : null}
                      {account.status === 'active' ? (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => {
                            setSuspendDialog(account);
                            setFailure(null);
                            setLastOwnerRefused(false);
                          }}
                        >
                          {copy.team.suspend}
                        </Button>
                      ) : account.status === 'disabled' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          busy={busyAction === `restore-${account.id}`}
                          onClick={() => {
                            runAction(`restore-${account.id}`, () =>
                              restoreAccount(account.id),
                            );
                          }}
                        >
                          {copy.team.restore}
                        </Button>
                      ) : null}
                      {account.status !== 'deauthorized' ? (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => {
                            setDeauthorizeDialog(account);
                            setFailure(null);
                            setLastOwnerRefused(false);
                          }}
                        >
                          {copy.team.deauthorize}
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ------------------------------------------------------ invitations */}
      <section
        aria-label={copy.team.invitations}
        style={{ marginBottom: 'var(--space-xl)' }}
      >
        <SectionHeading>{copy.team.invitations}</SectionHeading>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (inviteRole === null || inviteEmail.trim().length === 0) return;
            setInviteBusy(true);
            setInviteUrl(null);
            setCopied(false);
            setFailure(null);
            createInvitation({ email: inviteEmail.trim(), role: inviteRole })
              .then((result) => {
                setInviteBusy(false);
                setInviteUrl(result.invitationUrl);
                setInviteEmail('');
                reload();
              })
              .catch((thrown: unknown) => {
                setInviteBusy(false);
                setFailure(
                  thrown instanceof ApiFailure ? thrown : new ApiFailure('failed'),
                );
              });
          }}
          style={{
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-card)',
            padding: 'var(--space-md)',
            marginBottom: 'var(--space-md)',
            background: 'var(--surface-card)',
            maxWidth: 560,
          }}
        >
          <SectionHeading>{copy.team.invite}</SectionHeading>
          <TextField
            label={copy.team.inviteEmail}
            value={inviteEmail}
            onChange={setInviteEmail}
            type="email"
            dir="ltr"
            required
          />
          <ChoiceGroup
            legend={copy.team.inviteRole}
            value={inviteRole}
            onChange={setInviteRole}
            inline
            choices={ROLES.map((value) => ({
              value,
              label: copy.roles[value] ?? value,
            }))}
          />
          <Button variant="primary" type="submit" busy={inviteBusy}>
            {copy.team.inviteSubmit}
          </Button>

          {inviteUrl !== null ? (
            <InlineAlert
              tone="success"
              title={copy.team.inviteCreatedTitle}
              role="status"
            >
              <p style={{ marginBottom: 8 }}>{copy.team.inviteCreatedBody}</p>
              <p
                className="mj-ltr"
                dir="ltr"
                style={{
                  font: 'var(--type-body-tabular)',
                  overflowWrap: 'anywhere',
                  marginBottom: 8,
                }}
              >
                {inviteUrl}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  void navigator.clipboard.writeText(inviteUrl).then(() => {
                    setCopied(true);
                  });
                }}
              >
                {copied ? copy.team.linkCopied : copy.team.copyLink}
              </Button>
            </InlineAlert>
          ) : null}
        </form>

        {openInvitations.length === 0 ? (
          <EmptyState title={copy.team.invitationsEmpty} compact />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="mj-table">
              <thead>
                <tr>
                  <th scope="col">{copy.team.inviteEmail}</th>
                  <th scope="col">{copy.team.inviteRole}</th>
                  <th scope="col">{copy.contributions.columns.when}</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {openInvitations.map((invitation) => (
                  <tr key={invitation.id}>
                    <td>
                      <span
                        className="mj-ltr"
                        dir="ltr"
                        style={{ font: 'var(--type-body-tabular)' }}
                      >
                        {invitation.intendedEmail}
                      </span>
                    </td>
                    <td>
                      <Tag tone="neutral">
                        {copy.roles[invitation.role] ?? invitation.role}
                      </Tag>
                    </td>
                    <td>
                      <EventTime iso={invitation.expiresAt} />
                    </td>
                    <td style={{ textAlign: 'end' }}>
                      <Button
                        size="sm"
                        variant="danger"
                        busy={busyAction === `withdraw-${invitation.id}`}
                        onClick={() => {
                          runAction(`withdraw-${invitation.id}`, () =>
                            withdrawInvitation(invitation.id),
                          );
                        }}
                      >
                        {copy.team.withdraw}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* --------------------------------------------------- access history */}
      <section
        aria-label={copy.team.accessHistory}
        style={{ marginBottom: 'var(--space-xl)' }}
      >
        <SectionHeading>{copy.team.accessHistory}</SectionHeading>
        {history.length === 0 ? (
          <EmptyState title={copy.team.accessHistoryEmpty} compact />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="mj-table">
              <thead>
                <tr>
                  <th scope="col">{copy.team.accessHistoryColumns.when}</th>
                  <th scope="col">{copy.team.accessHistoryColumns.actor}</th>
                  <th scope="col">{copy.team.accessHistoryColumns.action}</th>
                  <th scope="col">{copy.team.accessHistoryColumns.subject}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      <EventTime iso={entry.occurredAt} />
                    </td>
                    <td>{entry.actor?.displayName ?? '—'}</td>
                    <td>
                      {/* The audit action code, verbatim: an identifier, not
                          copy, so it stays LTR and is never translated into
                          something the log does not say. */}
                      <span
                        className="mj-ltr"
                        dir="ltr"
                        style={{ font: 'var(--type-body-tabular)' }}
                      >
                        {entry.action}
                      </span>
                    </td>
                    <td>{entry.subject?.displayName ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ---------------------------------------------------------- dialogs */}

      <ConfirmDialog
        open={roleDialog !== null}
        title={copy.team.roleTitle(roleDialog?.displayName ?? '')}
        body={copy.team.roleBody}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.team.roleEffects}
        confirmLabel={copy.actions.confirm}
        cancelLabel={copy.actions.cancel}
        busy={busyAction === 'role'}
        error={
          lastOwnerRefused ? (
            <InlineAlert tone="warning" title={copy.team.lastOwnerTitle} role="alert">
              {copy.team.lastOwnerBody}
            </InlineAlert>
          ) : null
        }
        onCancel={() => {
          setRoleDialog(null);
        }}
        onConfirm={() => {
          if (roleDialog !== null && pendingRole !== null) {
            runAction(
              'role',
              () => changeAccountRole(roleDialog.id, pendingRole),
              () => {
                setRoleDialog(null);
              },
            );
          }
        }}
      >
        <ChoiceGroup
          legend={copy.team.inviteRole}
          value={pendingRole}
          onChange={setPendingRole}
          inline
          choices={ROLES.map((value) => ({ value, label: copy.roles[value] ?? value }))}
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={suspendDialog !== null}
        title={copy.team.suspendTitle}
        body={copy.team.suspendBody(suspendDialog?.displayName ?? '')}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.team.suspendEffects}
        confirmLabel={copy.team.suspend}
        cancelLabel={copy.actions.cancel}
        danger
        busy={busyAction === 'suspend'}
        error={
          lastOwnerRefused ? (
            <InlineAlert tone="warning" title={copy.team.lastOwnerTitle} role="alert">
              {copy.team.lastOwnerBody}
            </InlineAlert>
          ) : null
        }
        onCancel={() => {
          setSuspendDialog(null);
        }}
        onConfirm={() => {
          if (suspendDialog !== null) {
            runAction(
              'suspend',
              () => suspendAccount(suspendDialog.id),
              () => {
                setSuspendDialog(null);
                announce(copy.team.statuses['disabled'] ?? '');
              },
            );
          }
        }}
      />
      <ConfirmDialog
        open={deauthorizeDialog !== null}
        title={copy.team.deauthorizeTitle}
        body={copy.team.deauthorizeBody(deauthorizeDialog?.displayName ?? '')}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.team.deauthorizeEffects}
        confirmLabel={copy.team.deauthorize}
        cancelLabel={copy.actions.cancel}
        danger
        busy={busyAction === 'deauthorize'}
        error={
          lastOwnerRefused ? (
            <InlineAlert tone="warning" title={copy.team.lastOwnerTitle} role="alert">
              {copy.team.lastOwnerBody}
            </InlineAlert>
          ) : null
        }
        onCancel={() => {
          setDeauthorizeDialog(null);
        }}
        onConfirm={() => {
          if (deauthorizeDialog !== null) {
            runAction(
              'deauthorize',
              () => deauthorizeAccount(deauthorizeDialog.id),
              () => {
                setDeauthorizeDialog(null);
              },
            );
          }
        }}
      />
    </>
  );
}
